import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useI18n } from '@/lib/i18n';
import {
  LayoutDashboard, Inbox, BookOpen, Settings, Users, Shield,
  Building2, FileText, Cpu, Palette, Globe, ChevronDown, MessageSquare
} from 'lucide-react';
import { useState } from 'react';

const navLinkClass = ({ isActive }) =>
  `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
    isActive
      ? 'bg-[#EF694C]/10 text-[#EF694C] border border-[#EF694C]/20'
      : 'text-[#a1a1aa] hover:text-white hover:bg-white/5 border border-transparent'
  }`;

export function Sidebar() {
  const { user } = useAuth();
  const { t } = useI18n();
  const location = useLocation();
  const [settingsOpen, setSettingsOpen] = useState(location.pathname.startsWith('/settings'));
  const isSA = user?.role === 'superadmin';

  return (
    <div className="w-60 bg-[#09090b] border-r border-white/10 flex flex-col h-full" data-testid="sidebar">
      {/* Brand */}
      <div className="h-14 flex items-center gap-2.5 px-5 border-b border-white/10 flex-shrink-0">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#EF694C] to-[#d65d43] flex items-center justify-center shadow-[0_0_12px_rgba(239,105,76,0.4)]">
          <span className="text-white font-bold text-sm">F</span>
        </div>
        <div>
          <h1 className="text-white font-heading font-bold text-sm tracking-tight" data-testid="brand-name">FyreSupport</h1>
          <p className="text-[8px] text-[#52525b] uppercase tracking-widest font-medium">by OnFyre Marketing</p>
        </div>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {isSA ? (
          <>
            <NavLink to="/superadmin" end className={navLinkClass} data-testid="nav-control-plane">
              <LayoutDashboard className="w-4 h-4" /> {t.controlPlane}
            </NavLink>
            <NavLink to="/superadmin/tenants" className={navLinkClass} data-testid="nav-tenants">
              <Building2 className="w-4 h-4" /> {t.tenants}
            </NavLink>
            <NavLink to="/superadmin/audit" className={navLinkClass} data-testid="nav-audit">
              <FileText className="w-4 h-4" /> {t.auditLog}
            </NavLink>
            <NavLink to="/superadmin/feedback" className={navLinkClass} data-testid="nav-global-feedback">
              <MessageSquare className="w-4 h-4" /> {t.globalFeedback || 'Globales Feedback'}
            </NavLink>
          </>
        ) : (
          <>
            <NavLink to="/dashboard" className={navLinkClass} data-testid="nav-dashboard">
              <LayoutDashboard className="w-4 h-4" /> {t.dashboard}
            </NavLink>
            <NavLink to="/inbox" className={navLinkClass} data-testid="nav-inbox">
              <Inbox className="w-4 h-4" /> {t.inbox}
            </NavLink>
            <NavLink to="/knowledge" className={navLinkClass} data-testid="nav-knowledge">
              <BookOpen className="w-4 h-4" /> {t.knowledgeBase}
            </NavLink>

            {user?.role === 'tenant_admin' && (
              <>
                <div className="pt-3 pb-1">
                  <button onClick={() => setSettingsOpen(!settingsOpen)}
                    className="flex items-center justify-between w-full px-3 py-1.5 text-[10px] text-[#52525b] uppercase tracking-wider font-semibold hover:text-[#a1a1aa] transition-colors"
                    data-testid="settings-toggle">
                    <span>{t.settings}</span>
                    <ChevronDown className={`w-3 h-3 transition-transform ${settingsOpen ? 'rotate-180' : ''}`} />
                  </button>
                </div>
                {settingsOpen && (
                  <div className="space-y-1">
                    <NavLink to="/settings/channels" className={navLinkClass} data-testid="nav-channels">
                      <Globe className="w-4 h-4" /> {t.channels}
                    </NavLink>
                    <NavLink to="/settings/team" className={navLinkClass} data-testid="nav-team">
                      <Users className="w-4 h-4" /> {t.team}
                    </NavLink>
                    <NavLink to="/settings/ai" className={navLinkClass} data-testid="nav-ai">
                      <Cpu className="w-4 h-4" /> {t.aiPolicy}
                    </NavLink>
                    <NavLink to="/settings/branding" className={navLinkClass} data-testid="nav-branding">
                      <Palette className="w-4 h-4" /> {t.branding}
                    </NavLink>
                    <NavLink to="/settings/widget" className={navLinkClass} data-testid="nav-widget">
                      <Shield className="w-4 h-4" /> {t.widget}
                    </NavLink>
                    <NavLink to="/settings/feedback" className={navLinkClass} data-testid="nav-feedback">
                      <MessageSquare className="w-4 h-4" /> {t.pilotFeedback}
                    </NavLink>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </nav>

      {/* Footer */}
      <div className="px-5 py-3 border-t border-white/10">
        <p className="text-[10px] text-[#3f3f46] text-center">FyreSupport v1.0</p>
      </div>
    </div>
  );
}
